/**
 * Backward-compat barrel for migrated development path constants.
 *
 * Prefer importing from a subtree `*Paths.ts` under `domains/developments/` for new code.
 * This re-export keeps `RouteUrls.ts` and existing page/story/test imports working without churn.
 *
 * Do not import `domains/developments` (the route-tree aggregate) from URL helpers — that pulls page
 * `Component`s into the module graph and creates cycles.
 */

import { developmentChangeLogDetailsRoute, developmentChangeLogRoute } from "./developments/change-log/changeLogPaths";
import { developmentRoute } from "./developments/detail/developmentDetailPaths";
import { developmentReassignPurchaseOrdersRoute } from "./developments/full-page/fullPagePaths";
import { developmentsRoute } from "./developments/list/developmentsListPaths";
import {
  developmentAllLotsRoute,
  developmentDetailLegacyRoute,
  developmentDocumentsRoute,
  developmentDropsRoute,
  developmentJobLogNoteDrawerRoute,
  developmentJobLogsGalleryRoute,
  developmentJobLogsRoute,
  developmentLotConfigRoute,
  developmentLotDetailsRoute,
  developmentLotSequenceSheetRoute,
  developmentLotSiteConditionDetailsRoute,
  developmentLotSummaryReleaseRoute,
  developmentLotSummaryRoute,
  developmentTeamRoute,
} from "./developments/overview/overviewPaths";
import {
  developmentActivityRoute,
  developmentEditOptionsRoute,
  developmentOptionDataRoute,
  developmentPlanAndOptionsRoute,
  developmentPlanDataRoute,
  developmentPlanDetailsRoute,
} from "./developments/plan-and-options/planAndOptionsPaths";
import {
  developmentBidPackageRoute,
  developmentBidPackagesRoute,
  developmentContractLineItemsRoute,
  developmentContractOverviewRoute,
  developmentContractPurchaseOrdersRoute,
  developmentContractRoute,
  developmentProcurementRoute,
  developmentPurchaseOrdersRoute,
  developmentUnitPricingRoute,
} from "./developments/procurement/procurementPaths";
import {
  developmentProductOfferingApplyToProjectRoute,
  developmentProductOfferingConfigsCompareRoute,
  developmentProductOfferingConfigsRoute,
  developmentProductOfferingOptionalPath,
  developmentProductOfferingSpecificConfigsCompareRoute,
  developmentProductOfferingSpecificConfigsRoute,
  developmentProductOfferingsRoute,
  developmentProductOfferingVersionHistoryRoute,
} from "./developments/product-offerings/productOfferingsPaths";
import {
  developmentLegacyScheduleRoute,
  developmentLotSchedulesRoute,
  developmentScheduleRoute,
  developmentTaskLookaheadReportRoute,
} from "./developments/schedules/schedulesPaths";
import { developmentFeatureFlagTestRoute, developmentSettingsRoute } from "./developments/settings/settingsPaths";
import {
  developmentReviewTemplatesRoute,
  developmentScopeTemplateCostReportRoute,
  developmentScopeTemplateItemRoute,
  developmentScopeTemplateRoute,
  developmentScopeTemplatesRoute,
} from "./developments/templates/templatesPaths";

export * from "./developments/change-log/changeLogPaths";
export * from "./developments/commitments/commitmentsPaths";
export * from "./developments/detail/developmentDetailPaths";
export * from "./developments/full-page/fullPagePaths";
export * from "./developments/list/developmentsListPaths";
export * from "./developments/overview/overviewPaths";
export * from "./developments/plan-and-options/planAndOptionsPaths";
export * from "./developments/procurement/procurementPaths";
export * from "./developments/product-offerings/productOfferingsPaths";
export * from "./developments/schedules/schedulesPaths";
export * from "./developments/settings/settingsPaths";
export * from "./developments/templates/templatesPaths";

/** List + detail path prefixes (compat with legacy `routesDef` exports). */
export const developmentsPath = developmentsRoute.path;
export const developmentPath = developmentRoute.path;

/**
 * Stable string-path map used by `RouteUrls.ts` and existing tests/stories.
 *
 * Keys and values match the former `routesDef.developmentPaths` object.
 */
export const developmentPaths = {
  development: developmentPath,
  legacyScheduleRoute: developmentLegacyScheduleRoute.path,
  lotSchedules: developmentLotSchedulesRoute.path,
  developmentSchedule: developmentScheduleRoute.path,
  jobLogs: developmentJobLogsRoute.path,
  jobLogsGallery: developmentJobLogsGalleryRoute.path,
  jobLogNoteDrawer: developmentJobLogNoteDrawerRoute.path,
  detail: developmentDetailLegacyRoute.path,
  documents: developmentDocumentsRoute.path,
  team: developmentTeamRoute.path,
  drops: developmentDropsRoute.path,
  allLots: developmentAllLotsRoute.path,
  bidPackages: developmentBidPackagesRoute.path,
  bidPackage: developmentBidPackageRoute.path,
  contract: developmentContractRoute.path,
  contractLineItems: developmentContractLineItemsRoute.path,
  unitPricing: developmentUnitPricingRoute.path,
  contractOverview: developmentContractOverviewRoute.path,
  contractPurchaseOrders: developmentContractPurchaseOrdersRoute.path,
  lotConfig: developmentLotConfigRoute.path,
  lotSummary: developmentLotSummaryRoute.path,
  lotSequenceSheet: developmentLotSequenceSheetRoute.path,
  lotDetails: developmentLotDetailsRoute.path,
  lotSiteConditionDetails: developmentLotSiteConditionDetailsRoute.path,
  lotSummaryRelease: developmentLotSummaryReleaseRoute.path,
  procurement: developmentProcurementRoute.path,
  purchaseOrders: developmentPurchaseOrdersRoute.path,
  reassignPurchaseOrders: developmentReassignPurchaseOrdersRoute.path,
  scopeTemplate: developmentScopeTemplateRoute.path,
  scopeTemplateItem: developmentScopeTemplateItemRoute.path,
  scopeTemplateCostReport: developmentScopeTemplateCostReportRoute.path,
  scopeTemplates: developmentScopeTemplatesRoute.path,
  reviewTemplates: developmentReviewTemplatesRoute.path,
  planAndOptions: developmentPlanAndOptionsRoute.path,
  productOfferings: developmentProductOfferingsRoute.path,
  productOffering: developmentProductOfferingOptionalPath,
  productOfferingApplyToProject: developmentProductOfferingApplyToProjectRoute.path,
  productOfferingVersionHistory: developmentProductOfferingVersionHistoryRoute.path,
  productOfferingSpecificConfigs: developmentProductOfferingSpecificConfigsRoute.path,
  productOfferingConfigs: developmentProductOfferingConfigsRoute.path,
  productOfferingSpecificConfigsCompare: developmentProductOfferingSpecificConfigsCompareRoute.path,
  productOfferingConfigsCompare: developmentProductOfferingConfigsCompareRoute.path,
  planDetails: developmentPlanDetailsRoute.path,
  planData: developmentPlanDataRoute.path,
  optionData: developmentOptionDataRoute.path,
  activity: developmentActivityRoute.path,
  editOptions: developmentEditOptionsRoute.path,
  taskLookaheadReport: developmentTaskLookaheadReportRoute.path,
  settings: developmentSettingsRoute.path,
  featureFlagTest: developmentFeatureFlagTestRoute.path,
  changeLog: developmentChangeLogRoute.path,
  changeLogDetails: developmentChangeLogDetailsRoute.path,
};
